import { useState } from 'react'

import { useDeletePerson, useLoeschvorschau } from '../../hooks/queries'
import { useOnline } from '../../hooks/useOutbox'
import { groesse } from '../../lib/format'
import type { Person } from '../../api/types'

/** „1 Nachricht" / „3 Nachrichten" — die Pluralregel steht an einer Stelle. */
function worte(anzahl: number, eins: string, viele: string): string {
  return `${anzahl} ${anzahl === 1 ? eins : viele}`
}

/**
 * Ein Konto endgültig wegräumen (#47).
 *
 * Aus dem Issue: „Warnhinweis. Sicherheitsabfrage und Eingabe der E-Mail. nur
 * wenn richtig eingegeben wird auch gelöscht. … Vor dem Löschen eine Liste
 * anzeigen."
 *
 * **Zwei Schritte, und der erste ist keine Zierde.** Erst steht da, was genau
 * verschwindet — gezählt vom Server, nicht geraten —, dann muss die
 * E-Mail-Adresse abgetippt werden. Beides zusammen ist die Rückfrage; eine
 * gewöhnliche „Wirklich?"-Frage wäre hier zu wenig, weil es nichts
 * zurückzuholen gibt. Dieselbe Machart wie `trips/Zuruecksetzen.tsx`.
 *
 * **Die Adresse ist eine Bestätigung, kein Geheimnis.** Wer das hier sieht,
 * ist die Verwaltung und dürfte auch ohne. Sie soll verhindern, dass in einer
 * Liste mit zwanzig Konten das falsche erwischt wird.
 */
export function Kontoloeschung({
  person,
  onGeloescht,
}: {
  person: Person
  /**
   * **Was wegging, wird nach oben gemeldet und nicht hier gezeigt.**
   *
   * Hier stand der Befund selbst, in der Zeile der gelöschten Person — und
   * die Zeile verschwindet im selben Augenblick aus der Liste, weil die
   * Abfrage neu lädt. Wer den Satz zu sehen bekam, entschied damit ein
   * Rennen; der Browserlauf `kontoloeschen.mjs` verlor es zweimal
   * hintereinander und lief in eine Zeitüberschreitung. Eine Meldung, deren
   * Sichtbarkeit vom Zufall abhängt, ist keine.
   */
  onGeloescht: (befund: string) => void
}) {
  const [offen, setOffen] = useState(false)
  const [getippt, setGetippt] = useState('')
  const vorschau = useLoeschvorschau(person.id, offen)
  const loeschen = useDeletePerson()
  const online = useOnline()

  const zahlen = vorschau.data
  // Groß geschrieben und mit Leerzeichen zählt auch: iOS macht beides von
  // selbst. Der Server vergleicht genauso.
  const passt =
    zahlen !== undefined && getippt.trim().toLowerCase() === zahlen.email.trim().toLowerCase()
  const gesperrt = (zahlen?.eigene.length ?? 0) > 0

  return (
    <div className="stack" style={{ gap: 10 }}>
      {!offen && (
        <button
          type="button"
          className="button button--outline button--gefahr"
          style={{ padding: '6px 12px', fontSize: 14 }}
          onClick={() => {
            setGetippt('')
            setOffen(true)
          }}
        >
          Konto löschen …
        </button>
      )}

      {offen && (
        <div className="stack" style={{ gap: 12 }}>
          {vorschau.isPending && <p className="feld__hinweis">Wird gezählt …</p>}

          {vorschau.isError && (
            <p className="notice" role="alert">
              {vorschau.error instanceof Error
                ? vorschau.error.message
                : 'Die Übersicht lässt sich gerade nicht laden.'}
            </p>
          )}

          {zahlen !== undefined && (
            <>
              {/* **Was verschwindet, in Zahlen.** Erst danach kommt das Feld:
                  wer nicht gelesen hat, was es kostet, soll nicht tippen
                  können. */}
              <div>
                <p style={{ margin: '0 0 4px', fontWeight: 600 }}>Das verschwindet:</p>
                <ul
                  className="feld__hinweis feld__hinweis--13"
                  style={{ margin: 0, paddingLeft: 20 }}
                >
                  <li>{worte(zahlen.verfasst, 'geschriebene Nachricht', 'geschriebene Nachrichten')}</li>
                  {/* **Die Zahl, die überrascht.** Sie hängt genauso am
                      Fremdschlüssel — mit dem Konto gehen auch die Worte, die
                      der Reisende ihm geschrieben hat. */}
                  {zahlen.erhalten > 0 && (
                    <li>
                      {worte(zahlen.erhalten, 'Nachricht an ihn', 'Nachrichten an ihn')} — auch
                      vom Reisenden geschriebene
                    </li>
                  )}
                  <li>
                    {worte(zahlen.dateien, 'Datei', 'Dateien')}
                    {zahlen.bytes > 0 && ` · ${groesse(zahlen.bytes)}`}
                  </li>
                  <li>{worte(zahlen.reaktionen, 'Reaktion', 'Reaktionen')}</li>
                  <li>{worte(zahlen.geraete, 'angemeldetes Gerät', 'angemeldete Geräte')}</li>
                </ul>
              </div>

              {zahlen.reisen.length > 0 && (
                <div>
                  <p style={{ margin: '0 0 4px', fontWeight: 600 }}>Betroffene Reisen:</p>
                  <ul
                    className="feld__hinweis feld__hinweis--13"
                    style={{ margin: 0, paddingLeft: 20 }}
                  >
                    {zahlen.reisen.map((reise) => (
                      <li key={reise.title}>
                        {reise.title} — {worte(reise.nachrichten, 'Nachricht', 'Nachrichten')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* **Die eine Sperre, die bleibt.** Sie stünde sonst mitsamt
                  allen Mitlesenden zur Disposition, die niemand gefragt hat.
                  Der Grund steht da, statt dass ein Knopf einfach fehlt. */}
              {gesperrt ? (
                <p className="notice" role="alert">
                  Diesem Konto gehören noch Reisen: {zahlen.eigene.join(', ')}. Erst die
                  Reise, dann das Konto. Bis dahin bleibt nur das Stilllegen.
                </p>
              ) : (
                <>
                  <p className="notice" role="alert">
                    <strong>Rückgängig geht das nicht.</strong> Was diese Person
                    geschrieben hat, verschwindet aus jedem Reisebericht, in dem
                    es steht.
                  </p>

                  <label className="field">
                    <span className="field__label">
                      Zum Bestätigen abtippen: <strong>{zahlen.email}</strong>
                    </span>
                    <input
                      className="field__input"
                      value={getippt}
                      onChange={(event) => setGetippt(event.target.value)}
                      autoCapitalize="off"
                      autoCorrect="off"
                      autoComplete="off"
                      spellCheck={false}
                      inputMode="email"
                      aria-label={`E-Mail-Adresse ${zahlen.email} abtippen`}
                      placeholder={zahlen.email}
                    />
                  </label>
                </>
              )}

              {loeschen.isError && (
                <p className="notice" role="alert">
                  {loeschen.error instanceof Error
                    ? loeschen.error.message
                    : 'Das hat nicht geklappt.'}
                </p>
              )}

              {/* Ohne Netz abgeschaltet, mit Grund als Text — ein Tooltip ist
                  auf dem Telefon unsichtbar. */}
              {!online && !gesperrt && (
                <p className="feld__hinweis" role="alert">
                  Ohne Netz geht das gerade nicht.
                </p>
              )}

              <div className="row" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="button button--outline button--small"
                  onClick={() => setOffen(false)}
                >
                  Abbrechen
                </button>
                {!gesperrt && (
                  <button
                    type="button"
                    className="button button--outline button--small button--gefahr"
                    disabled={!passt || !online || loeschen.isPending}
                    onClick={async () => {
                      const weg = await loeschen.mutateAsync({
                        id: person.id,
                        email: getippt.trim(),
                      })
                      setOffen(false)
                      setGetippt('')
                      // **Gesagt wird, was wirklich wegging** -- die Zahlen der
                      // Antwort und nicht die der Vorschau davor.
                      onGeloescht(
                        `„${zahlen.name}" ist gelöscht: `
                          + `${worte(weg.nachrichten, 'Nachricht', 'Nachrichten')} und `
                          + `${worte(weg.dateien, 'Datei', 'Dateien')} sind weg.`,
                      )
                    }}
                  >
                    {loeschen.isPending ? 'Wird gelöscht …' : 'Endgültig löschen'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}

    </div>
  )
}
